import { Link } from 'react-router-dom';
import type { Tone } from '../domain/workstations';
import { CONNECTION_LABELS, isDesktop } from './bridge';
import { useSyncStatus } from './useSyncStatus';

/**
 * Indicateur de connexion, visible en permanence dans la barre.
 *
 * Trois situations, et pas davantage :
 *   — connecté et à jour ;
 *   — connecté et en cours de synchronisation ;
 *   — hors ligne, avec le nombre d'éléments en attente.
 *
 * Un quatrième cas se glisse au-dessus des trois : le serveur répond mais
 * refuse la session. Le poste n'est alors pas hors ligne, il est déconnecté,
 * et attendre n'y changera rien — le dire évite qu'on attende un retour de
 * réseau qui a déjà eu lieu.
 *
 * Rend `null` dans le navigateur.
 */
export function ConnectionBadge() {
  const status = useSyncStatus();
  if (!isDesktop() || status === null) return null;

  const tone: Tone =
    status.state === 'horsLigne'
      ? 'danger'
      : status.state === 'synchronisation'
        ? 'alerte'
        : 'ok';

  const attente = status.pending === 0 ? '' : ` · ${status.pending} en attente`;
  const echecs = status.failed === 0 ? '' : ` · ${status.failed} en échec`;

  const libelle = !status.authenticated
    ? 'Session à refaire'
    : `${CONNECTION_LABELS[status.state]}${attente}`;

  // L'état affiché tient compte du canal en direct : un canal ouvert prouve
  // que le serveur répond à l'instant même. L'indicateur de connectivité du
  // système, lui, n'est jamais consulté — il dirait « en ligne » sur un réseau
  // de chantier sans route vers le serveur.
  const liaison =
    status.live === 'connecte'
      ? 'Liaison en direct établie.'
      : status.live === 'connexion'
        ? 'Liaison en direct en cours d’établissement.'
        : 'Liaison en direct rompue — reprise automatique.';

  return (
    <Link
      to="/hors-ligne"
      className={`pastille pastille--${!status.authenticated ? 'danger' : tone} pastille--lien`}
      title={[
        status.lastError ??
          (status.lastSyncAt === null
            ? 'Aucune synchronisation depuis le démarrage.'
            : `Dernière synchronisation : ${new Date(status.lastSyncAt).toLocaleTimeString('fr-FR')}`),
        liaison,
      ].join('\n')}
    >
      {libelle}
      {echecs}
    </Link>
  );
}
